import { Box, Text } from "rebass/styled-components";
import { Label } from "@rebass/forms/styled-components";

import Heading from "../../common/Heading";
import AutocompleteInput from "../../common/AutocompleteInput";
import Input from "../../common/Input";
import useTranslation from "next-translate/useTranslation";
import { Formik } from "formik";
import { useState, useEffect } from "react";
import { CityInput } from "../../../interfaces";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { gql } from "apollo-boost";
import { useQuery, useMutation } from "@apollo/client";
import ProfilePictureUploader from "../ProfilePictureUploader";
import { FullLoader } from "../../common/loader";
import { ToastProvider } from "react-toast-notifications";
import { WarningToast } from "../../common/toasts";
import { AutoSubmit } from "../../common/forms";

const query = gql`
  query getCurrentUser {
    currentUser {
      id
      name
      surname
      phone
      city {
        id
        name
        placeId
        latitude
        longitude
      }
      avatar {
        circle
      }
    }
    country {
      id
      iso3661Alpha2
    }
  }
`;

const mutation = gql`
  mutation onboardOwner($input: OnboardOwnerInput!) {
    onboardOwner(input: $input) {
      user {
        id
        name
        surname
        city {
          id
          name
          placeId
          latitude
          longitude
        }
        phone
        avatar {
          circle
        }
      }
      errors {
        message
        path
      }
    }
  }
`;

interface Props {
  submitDone: (x: any) => void;
  triggerSubmit: boolean;
}

interface OnboardOwner {
  name: string;
  surname: string;
  avatar: string;
  city: CityInput;
  phone: string;
}

const PersonalInfo = (props: Props) => {
  const { t } = useTranslation("user/registration");
  const { submitDone, triggerSubmit } = props;

  const [state, setState] = useState({
    city: {} as CityInput
  } as OnboardOwner);

  const { data } = useQuery(query);
  const [onboardOwner, { loading }] = useMutation(mutation, {
    refetchQueries: ["currentUser"]
  });

  const telephone = new AsYouType(data?.country.iso3661Alpha2);

  useEffect(() => {
    if (data) {
      const { currentUser } = data;
      const { city } = currentUser;

      setState({
        name: currentUser.name,
        surname: currentUser.surname,
        city: {
          ...city,
          id: undefined,
          __typename: undefined
        },
        phone: currentUser.phone,
        avatar: currentUser?.avatar?.circle
      });
    }
  }, [data]);

  const validate = values => {
    let errors = {};
    if (!values.name) {
      errors["name"] = t("common:required");
    }

    if (!values?.city.placeId) {
      errors["city"] = t("common:required");
    }

    if (values?.phone?.length > 1) {
      if (
        !parsePhoneNumberFromString(
          values.phone,
          telephone.country
        ).isPossible()
      ) {
        errors["phone"] = t("common:phoneFormat");
      }
    }

    return errors;
  };

  const submit = async values => {
    await onboardOwner({
      variables: {
        input: {
          ...values,
          phone: telephone.getNumber()?.number || values.phone //making sure phone uploads with country code
        }
      }
    });

    submitDone(values?.city);
  };

  return (
    <>
      <ToastProvider components={{ Toast: WarningToast }}>
        {loading && <FullLoader />}
        <Heading as="h3" fontWeight="bold">
          {t("personalInfo")}
        </Heading>
        <Text as="p" mt={2} mb={3}>
          {t("myDogsDescription")}
        </Text>
        <Formik
          enableReinitialize={true}
          initialValues={state}
          validate={validate}
          onSubmit={submit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            setFieldValue
          }) => {
            return (
              <>
                {data && (
                  <ProfilePictureUploader
                    currentUser={data.currentUser}
                    avatar={values.avatar}
                    onChange={avatar => setFieldValue("avatar", avatar)}
                  />
                )}
                <Box as="form" onSubmit={handleSubmit}>
                  <Box
                    width={1}
                    variant={touched.name && errors.name && "error"}
                  >
                    <Label flexWrap="wrap" mb={1}>
                      <Text mb={1}>{t("name")} *</Text>
                      <Box width={1}>
                        <Input
                          key="name"
                          type="text"
                          name="name"
                          id="frmNameCC"
                          placeholder={t("name")}
                          value={values.name || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.name && touched.name && (
                          <Text as="span">{`${errors.name}`}</Text>
                        )}
                      </Box>
                    </Label>
                  </Box>
                  <Box
                    width={1}
                    mt={3}
                    variant={touched.surname && errors.surname && "error"}
                  >
                    <Label flexWrap="wrap" mb={1}>
                      <Text mb={1}>{t("surname")}</Text>
                      <Box width={1}>
                        <Input
                          key="surname"
                          type="text"
                          name="surname"
                          placeholder={t("surname")}
                          value={values.surname || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {errors.surname && touched.surname && (
                          <Text as="span">{`${errors.surname}`}</Text>
                        )}
                      </Box>
                    </Label>
                  </Box>
                  <Box
                    width={1}
                    mt={3}
                    sx={{ zIndex: 1 }}
                    variant={touched.city && errors.city && "error"}
                  >
                    <Label flexWrap="wrap" mb={1}>
                      <Text mb={1}>{t("city")} *</Text>
                      <AutocompleteInput
                        label={t("city")}
                        name={"city.name"}
                        key={"city"}
                        value={values.city.name || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSelect={data => {
                          setFieldValue("city", {
                            name: data.near,
                            placeId: data.pid,
                            latitude: data.lat,
                            longitude: data.lng
                          });
                        }}
                      />
                      {errors.city && touched.city && (
                        <Text as="span">{`${errors.city}`}</Text>
                      )}
                    </Label>
                  </Box>
                  <Box
                    width={1}
                    mt={3}
                    variant={touched.phone && errors.phone && "error"}
                  >
                    <Label flexWrap="wrap" mb={1}>
                      <Text width={1} mb={1}>
                        {t("telephone")}
                      </Text>
                      <Box width={1}>
                        <Input
                          type="tel"
                          placeholder={t("telephone")}
                          name="phone"
                          value={values.phone || ""}
                          onChange={e => {
                            telephone.reset();
                            const number = telephone.input(e.target.value);
                            e.target.value = number;
                            handleChange(e);
                          }}
                          data-test="telephone"
                        />
                      </Box>
                      {errors.phone && touched.phone && (
                        <Text as="span">{`${errors.phone}`}</Text>
                      )}
                    </Label>
                  </Box>
                  <AutoSubmit submitOnNextRender={triggerSubmit} />
                </Box>
              </>
            );
          }}
        </Formik>
      </ToastProvider>
    </>
  );
};

export default PersonalInfo;
